import { useEventStore } from '../store/event';
import { LoadEvent, FetchEvent } from '../types/event';
import { PageKeyMap, PageLoadedSelectorsMap } from '../types/setup';
import {
  getFetchEventsDuration,
  getOpAggregateErrorsCounter,
  getOpDurationHistogram,
  getOpErrorsCounter,
  getOpNetworkDurationHistogram,
  getOpSloErrorsCounter,
  getOpTotalCounter,
} from '../utils/metrics';
import { loadSLOThreshold } from '../constants/setup';
import { getPathKey, getPathName, isInvalidPageKey } from '../utils/url';
import { getExtendedAttributes } from '../utils/setup';
import { diag } from '@opentelemetry/api';
import { loggerProvider } from '@/telemetry-sdk/setup/csr';
import { emitOtelLog } from '@/telemetry-sdk/utils/logging';

const otelLogger = diag.createComponentLogger({
  namespace: '✨ @telemetry-sdk/services/event',
});

// services
const handleInit = (pageKeyMap: PageKeyMap, pageLoadedSelectorsMap: PageLoadedSelectorsMap, userId: string) => {
  otelLogger.info('Handle Init:', pageKeyMap);
  const { initStore } = useEventStore.getState();
  const newPagePath = getPathName(window.location.href);
  const newPageKey = getPathKey(newPagePath, pageKeyMap);
  initStore(newPageKey, newPagePath, pageKeyMap, pageLoadedSelectorsMap, userId);
};

const handleNavigate = (pageUrl: string) => {
  const { pageKeyMap, loadEvents, resetStore } = useEventStore.getState();

  // get new page path and key
  const newPagePath = getPathName(pageUrl);
  const newPageKey = getPathKey(newPagePath, pageKeyMap);

  // return early if pagePath is the same
  otelLogger.info('Handle Navigate:', pageUrl);

  // reset store if pagePath is different
  resetStore(newPageKey, newPagePath);

  // add custom event
  if (loadEvents.find((event) => event.type === 'navigate')) return;
  const nextNavigateEvent: LoadEvent = {
    type: 'navigate',
    timeStamp: performance.now(),
  };
  handleLoadEvent(nextNavigateEvent);
};

const handleLoadEvent = (loadEvent: LoadEvent, traceId?: string) => {
  otelLogger.info('Handle Load Event:', loadEvent);
  const { addLoadEvent } = useEventStore.getState();

  if (loadEvent.type == 'rendered') {
    addLoadEvent(loadEvent);
    handleLoaded(traceId);
  } else {
    addLoadEvent(loadEvent);
  }
};

const handleFetchEvent = (fetchEvent: FetchEvent) => {
  otelLogger.info('Handle Fetch Event:', fetchEvent);
  const { addFetchEvent } = useEventStore.getState();

  addFetchEvent(fetchEvent);
};

const handleLoaded = (traceId: string | undefined) => {
  const { userId, pageKey, loadEvents, fetchEvents, isLoaded } = useEventStore.getState();

  // return early if already loaded
  if (isLoaded) return;
  if (isInvalidPageKey(pageKey)) return;

  otelLogger.info('Handle Loaded', loadEvents);

  // init vars
  const initialNavigateEvent = loadEvents.find((event) => event.type === 'initial');
  const nextNavigateEvent = loadEvents.find((event) => event.type === 'navigate');
  const renderedEvent = loadEvents.find((event) => event.type === 'rendered');
  const currentMeterCarrier = window?.meterCarrier ?? window.__NEXT_DATA__?.props?.pageProps?.meterCarrier;

  // return early if not ready
  if (!nextNavigateEvent || !renderedEvent) return;
  useEventStore.getState().setIsLoaded(true);
  otelLogger.info('Handle Loaded: Logging Metrics', loadEvents);

  // calc - load durations
  // if initialNavigateEvent exists, it means its a fresh reload and start from 0, if not, then we start from when navigation is triggered
  const startTimeMs = initialNavigateEvent ? initialNavigateEvent.timeStamp : nextNavigateEvent.timeStamp;
  const renderedTimeMs = renderedEvent.timeStamp;
  const loadDurationMs = renderedTimeMs - startTimeMs;

  // calc - fetch durations
  const filteredFetchEvents = fetchEvents.filter((event) => (event.endTime ? event.endTime <= renderedTimeMs : false));
  const ssrFetchDurationMs = currentMeterCarrier?.ssrFetchDurationMs ?? 0;
  const csrFetchDurationMs = getFetchEventsDuration(filteredFetchEvents);
  const fetchDurationMs = ssrFetchDurationMs + csrFetchDurationMs;

  // calc - fetch errors
  const hasCsrFetchErrors = filteredFetchEvents.some((event) => event.isError);
  const hasSsrFetchErrors = (currentMeterCarrier?.backendFetchErrors ?? 0) > 0;
  const hasFetchErrors = hasCsrFetchErrors || hasSsrFetchErrors;

  // calc - SLO errors
  const hasSloError = loadDurationMs > loadSLOThreshold;
  const hasAggregateError = hasFetchErrors || hasSloError;

  // metric - op total counter
  const pageOpTotalCounter = getOpTotalCounter(pageKey);
  pageOpTotalCounter.add(1, getExtendedAttributes(pageKey, userId));

  // metric - op errors
  if (hasFetchErrors) {
    const pageOpErrorsCounter = getOpErrorsCounter(pageKey);
    pageOpErrorsCounter.add(1, getExtendedAttributes(pageKey, userId));
  }

  // metric - op duration
  const pageOpDurationHistogram = getOpDurationHistogram(pageKey);
  pageOpDurationHistogram.record(loadDurationMs / 1000, getExtendedAttributes(pageKey, userId));

  // metric - op network duration
  const pageOpNetworkDurationHistogram = getOpNetworkDurationHistogram(pageKey);
  pageOpNetworkDurationHistogram.record(fetchDurationMs / 1000, getExtendedAttributes(pageKey, userId));

  // metric - op slo counter
  if (hasSloError) {
    const opSloErrorsCounter = getOpSloErrorsCounter(pageKey);
    opSloErrorsCounter.add(1, getExtendedAttributes(pageKey, userId));
    emitOtelLog('SLO error occurred.', pageKey, userId, traceId);
  }

  // metric - op aggregate counter
  if (hasAggregateError) {
    const opAggregateErrorsCounter = getOpAggregateErrorsCounter(pageKey);
    opAggregateErrorsCounter.add(1, getExtendedAttributes(pageKey, userId));
    emitOtelLog('Aggregate error occurred.', pageKey, userId, traceId);
  }
};

export { handleInit, handleNavigate, handleLoadEvent, handleFetchEvent };
